import {
  type Attributes,
  cloneElement,
  type Component,
  forwardRef,
  type JSX,
  type PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import { cx } from '@linaria/core';
import { Disclosure, DisclosureContent, type DisclosureOptions, useDisclosureState } from 'reakit/Disclosure';

import { defaultAnimation } from './style';

type AccordionProps = PropsWithChildren<{
  animationClass?: boolean | string;
  header: JSX.Element;
  id: string | number;
  /**
   * Fired after the user toggles the accordion. Receives the new visible state.
   */
  onToggle?: (visible: boolean) => void;
  visible?: boolean;
}>;

export const Accordion = forwardRef<Component<'button', DisclosureOptions>, AccordionProps>(function Accordion(
  { animationClass = true, children, header, id, onToggle, visible = false },
  ref,
) {
  const animated = animationClass === true || (typeof animationClass === 'string' && animationClass !== '');

  const disclosure = useDisclosureState({
    animated: animated ? 300 : false,
    baseId: id.toString(),
    visible,
  });
  const finalAnimationClass =
    typeof animationClass === 'string' && animationClass !== '' ? animationClass : defaultAnimation;

  const previousVisibleRef = useRef(disclosure.visible);
  useEffect(() => {
    if (previousVisibleRef.current !== disclosure.visible) {
      previousVisibleRef.current = disclosure.visible;
      onToggle?.(disclosure.visible);
    }
  }, [disclosure.visible, onToggle]);

  return (
    <>
      <Disclosure {...disclosure} ref={ref} {...header.props}>
        {(disclosureProps: (Partial<any> & Attributes) | undefined) => cloneElement(header, disclosureProps)}
      </Disclosure>
      <DisclosureContent
        {...disclosure}
        className={cx(animated && finalAnimationClass)}
        {...(visible ? { 'data-enter': true } : {})}
      >
        {children}
      </DisclosureContent>
    </>
  );
});
